'use client';

import { useOverlay } from '@flipster/overlay';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';

import { LuckyDrawItemFragment } from '@web/app/[locale]/campaign/[slug]/query.generated';
import { Asset } from '@web/graphql/types.generated';

import { slugToNameMapper } from '../constants';
import { ConvertModal } from '../convert-modal';
import { useSpookyTreatsFestivalLuckyDraw } from '../useLuckyDraw';
import { HandleExchangeProps, ItemCard } from './item-card';
import { TopButton } from './top-button';

interface Props {
  luckyDrawData: LuckyDrawItemFragment[];
}

export const TicketOverview = ({ luckyDrawData }: Props) => {
  const { isRegistered, registerLuckyDraw, status } = useSpookyTreatsFestivalLuckyDraw();
  const overlay = useOverlay();

  const handleExchange = ({ fromSlug, toSlug, cost }: HandleExchangeProps) => {
    if (!isRegistered) {
      registerLuckyDraw();
      return;
    }
    overlay.open(() => <ConvertModal fromSlug={fromSlug} toSlug={toSlug} cost={cost} />);
  };

  const handleViewMissions = () => {
    const howToGetSection = document.getElementById('how-to-get');
    if (howToGetSection) {
      requestAnimationFrame(() => {
        const rect = howToGetSection.getBoundingClientRect();
        const top = window.pageYOffset + rect.top - 106;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  };

  return (
    <div className={css({ pb: { base: 64, tablet: 80 } })}>
      <TopButton />
      <h2
        className={css({
          textStyle: 'Display/XL',
          color: 'content.default.level-4',
          mt: { base: 64, tablet: 80 },
        })}
      >
        Win 1 ETH, 1 BNB, 1,000 USDT & more!
      </h2>
      <p
        className={css({
          textStyle: 'Title/S',
          color: status === 'FINISHED' ? 'content.state.warning' : 'content.default.level-3',
          whiteSpace: 'pre-line',
          mt: { base: 12, tablet: 16 },
        })}
      >
        {status !== 'FINISHED'
          ? 'The more sweets you earn, the higher your chances of winning.'
          : 'The event has ended. Stay tuned for rewards.'}
      </p>
      {status !== 'FINISHED' && (
        <p
          className={css({
            textStyle: 'Text/L/Regular',
            color: 'content.default.level-3',
            textAlign: 'center',
            pt: 8,
          })}
        >
          *Sweets are offered about 5 minutes after a mission is complete.
        </p>
      )}
      <ul
        className={flex({
          flexDirection: { base: 'column', tablet: 'row' },
          gap: { base: 8, tablet: 36 },
          justify: 'space-between',
          mt: 40,
        })}
      >
        {luckyDrawData.map((item, index) => (
          <ItemCard
            key={item.slug ? slugToNameMapper[item.slug].singular : ''}
            fromSlug={luckyDrawData[index - 1]?.slug ?? ''}
            toSlug={item.slug ?? ''}
            tokenIcon={item.tokenIcon as Asset}
            index={index}
            handleButtonClick={index === 0 ? handleViewMissions : handleExchange}
          />
        ))}
      </ul>
    </div>
  );
};
